import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, X, Star, Eye, EyeOff, Save } from "lucide-react";

const empty = { author: "", role: "", rating: 5, text: "", published: true };

export default function ReviewsView() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/reviews/all");
    setReviews(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) {
        await api.patch(`/reviews/${form.id}`, form);
        toast.success("Recensione aggiornata");
      } else {
        await api.post("/reviews", form);
        toast.success("Recensione aggiunta");
      }
      setShowForm(false);
      setForm(empty);
      load();
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const togglePub = async (r) => {
    await api.patch(`/reviews/${r.id}`, { published: !r.published });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    await api.delete(`/reviews/${id}`);
    toast.success("Eliminata");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Star size={16} className="text-amber-400 fill-amber-400" />
            <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
              Recensioni clienti
            </h2>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Testimonianze dei tuoi clienti. Le recensioni pubblicate appaiono sulla home per aumentare la fiducia dei visitatori.
          </p>
        </div>
        <button
          data-testid={testIds.adminAddReviewBtn}
          onClick={() => {
            setForm(empty);
            setShowForm(true);
          }}
          className="min-h-[44px] px-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition flex items-center gap-2"
        >
          <Plus size={14} /> Nuova recensione
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 text-xs font-mono uppercase tracking-widest border border-dashed border-zinc-800 rounded-xl">
          Nessuna recensione ancora — aggiungi la prima
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {reviews.map((r) => (
            <div
              key={r.id}
              className={`bg-zinc-900/90 border rounded-xl p-4 ${r.published ? "border-zinc-800" : "border-amber-900/40 opacity-70"}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-white text-sm">
                      {r.author}
                    </span>
                    <span className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={11}
                          className={
                            i < r.rating
                              ? "text-amber-400 fill-amber-400"
                              : "text-zinc-700"
                          }
                        />
                      ))}
                    </span>
                    {!r.published && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-mono uppercase tracking-widest">
                        nascosta
                      </span>
                    )}
                  </div>
                  {r.role && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                      {r.role}
                    </div>
                  )}
                  <p className="text-sm text-zinc-300 mt-2 break-words">&ldquo;{r.text}&rdquo;</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => togglePub(r)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition"
                    aria-label={r.published ? "Nascondi" : "Pubblica"}
                  >
                    {r.published ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-rose-950 text-zinc-400 hover:text-rose-400 transition"
                    aria-label="Elimina"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowForm(false)}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-black uppercase text-white">
                Nuova recensione
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <input
                data-testid={testIds.adminReviewAuthor}
                required
                placeholder="Nome cliente (es. Giulia M.)"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
              />
              <input
                placeholder="Ruolo/evento (es. Sposa — Matrimonio a Como)"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
              />
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Stelle
                </label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      className="p-1"
                    >
                      <Star
                        size={20}
                        className={
                          n <= form.rating
                            ? "text-amber-400 fill-amber-400"
                            : "text-zinc-700"
                        }
                      />
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                data-testid={testIds.adminReviewText}
                required
                rows={4}
                placeholder="Testo della recensione..."
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full px-3.5 py-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm resize-none"
              />
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 min-h-[48px] rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider transition"
              >
                Annulla
              </button>
              <button
                data-testid={testIds.adminReviewSave}
                type="submit"
                className="flex-1 min-h-[48px] rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition flex items-center justify-center gap-2"
              >
                <Save size={14} /> Salva
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
